import { useEffect, useRef, useState } from "react";
import RecipientField from "components/Reusable/SideBar/Share/RecipientField.js";
import Tooltip from "components/Elements/Tooltip/Tooltip.js";
import Icon from "../../../Elements/Icon/Icon.tsx";
import useMediaQueries from "../../../../hooks/useMediaQueries.js";
import useAvoidFirstMount from "hooks/useMountedState.js";
import SaveMailComponent from "../Reusable/SaveMailComponent.js";
import UserEmailInput from "./UserEmailInput.js";
import useMoveableLabel from "./useMoveableLabel.js";
import RichTextEditorField from "components/Reusable/Form/RichTextEditorField.js";
import SettingsItem from "components/Reusable/Form/Reusable/SettingField.tsx";
import SecurityPasswordField from "components/Reusable/Form/Reusable/SecurityPasswordField.tsx";
import DownloadLimit from "components/Reusable/Form/Reusable/DownloadLimitField.tsx";
import CollaborationMailField from "components/Reusable/Form/Reusable/CollaborationMailField.tsx";
import { EMAIL_VALIDATOR } from "helpers/validators.js";
import { useCustomController } from "components/Reusable/Form/useCustomController.js";
import { useContent } from "../../../../contexts/contentContext/contentContext.js";
import { useUser } from "../../../../contexts/userContext/userContext.js";
import { PaymentAmount } from "../../../Pages/Inbox/Popups/PaymentSettings.js";
import { UPLOADER_SIDEBAR_MOUNT_DELAY } from "../../../../utils/globalConsts.ts";
import DisplayVideoField from "components/Reusable/Form/Reusable/DisplayVideoField.tsx";

const URL_DETECTOR_ARRAY = [
  "https://",
  "http://",
  "http://www/.",
  "https://www/.",
];

// setBodyValue function for homepage usage only
export const setBodyValue = (props, value) => {
  if (props.externalProps?.setBody) {
    props.externalProps.setBody((prev) => ({
      ...prev,
      [props.item.fieldKey]: value,
    }));
  }
};

const PaymentOwnerName = (props) => {
  // check if value set in form
  const moreThanZero = props.getValues().PaymentAmount;
  const cutomValidation = () => {
    return props.getValues().PaymentAmount ? false : true;
  };
  const { getUserData } = useUser();

  const { fieldControls, hasError } = useCustomController({
    ...props,
    cutomValidation: { checkDisableValidation: cutomValidation },
  });

  const { getTextByKey } = useContent();

  const [value, setValue] = useState(fieldControls.value);

  useEffect(() => {
    fieldControls.onChange(value);
    setBodyValue(props, value);
  }, [value]);

  useAvoidFirstMount({
    callback: () => {
      if (!moreThanZero) {
        getUserData()?.UserFirstName
          ? setValue(
              getUserData().UserFirstName + " " + getUserData().UserLastName
            )
          : setValue("");
      }
    },
    dependencies: [moreThanZero],
  });

  return (
    <>
      <label
        className={`border-bottom-only${hasError ? " invalid-input" : ""}`}
        style={{ marginBottom: "10px" }}
      >
        <div className={`input-label hidden`}>
          {getTextByKey("login-user-name-label")}
        </div>
        <input
          className="input-field none payments"
          name="PaymentUserEmail"
          placeholder={getTextByKey("displayname-label-text")}
          autoComplete="PaymentUserEmail"
          onChange={(e) => setValue(e.target.value)}
          value={fieldControls.value}
          disabled={!moreThanZero}
        />
        <div className={"tooltip-container"}>
          <Tooltip tooltipText={getTextByKey("inbox-payment-name-tooltip")}>
            <Icon
              name="info"
              gray
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            />
          </Tooltip>
        </div>
      </label>
    </>
  );
};

const SubjectInput = (props) => {
  const { fieldControls, hasError } = useCustomController(props);
  const { getTextByKey } = useContent();
  const [value, setValue] = useState(fieldControls.value);
  const inputRef = useRef();
  const { mobileCondition } = useMediaQueries();

  useEffect(() => {
    fieldControls.onChange(value);
    setBodyValue(props, value);
  }, [value]);

  useEffect(() => {
    if (inputRef?.current && !mobileCondition) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, UPLOADER_SIDEBAR_MOUNT_DELAY);
    }
  }, [inputRef?.current, mobileCondition]);

  const { labelClassName } = useMoveableLabel({
    label: props.item.label,
    value,
  });

  return (
    <label className={`border-bottom-only${hasError ? " invalid-input" : ""}`}>
      <div className={labelClassName}>{getTextByKey("subject")}</div>
      <input
        className={`input-field`}
        name="ShareToEmailSubjectTitle"
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        ref={inputRef}
      />
    </label>
  );
};

export const generateLinkFields = {
  SendOwnerEmail: {
    element: "customelement",
    fieldKey: "SendOwnerEmail",
    label: "moveable",
    validation: {
      required: true,
      invalidMessage: "invalid-email-text",
      reg: EMAIL_VALIDATOR,
    },
    render: (props) => <UserEmailInput {...props} viaLink={true} />,
  },
  ShareToEmailSubjectTitle: {
    element: "customelement",
    fieldKey: "ShareToEmailSubjectTitle",
    label: "moveable",
    render: (props) => <SubjectInput {...props} viaLink={true} />,
  },
};

export const sendFields = {
  SendToEmail: {
    element: "customelement",
    fieldKey: "SendToEmail",
    label: "moveable",
    validation: {
      required: true,
      invalidMessage: "invalid-email-text",
      reg: EMAIL_VALIDATOR,
    },
    render: (props) => <RecipientField {...props} />,
  },
  SendOwnerEmail: {
    element: "customelement",
    fieldKey: "SendOwnerEmail",
    label: "moveable",
    validation: {
      required: true,
      invalidMessage: "invalid-email-text",
      reg: EMAIL_VALIDATOR,
    },
    render: (props) => <UserEmailInput {...props} />,
  },
  ShareToEmailSubjectTitle: {
    element: "customelement",
    fieldKey: "ShareToEmailSubjectTitle",
    label: "moveable",
    render: (props) => <SubjectInput {...props} />,
  },
  ShareToEmailBodyTitle: {
    element: "customelement",
    fieldKey: "ShareToEmailBodyTitle",
    label: "moveable",
    validation: {
      validate: (value) =>
        !URL_DETECTOR_ARRAY.some((url) => value?.includes(url)) ||
        "suspicious-body-text",
      maxLength: 5000,
    },
    render: (props) => (
      <RichTextEditorField {...props} setBodyValue={setBodyValue} />
    ),
  },
};

export const paymentFields = {
  PaymentAmount: {
    element: "customelement",
    fieldKey: "PaymentAmount",
    validation: {
      validate: (value) =>
        value === "" || value >= 10 || "min-required-amount-invalid-message",
      invalidMessage: "min-required-amount-invalid-message",
    },
    render: (props) => <PaymentAmount {...props} setBodyValue={setBodyValue} />,
  },
  PaymentUserEmail: {
    fieldKey: "PaymentUserEmail",
    element: "customelement",
    validation: {
      required: true,
      reg: /[^\n]+/,
      invalidMessage: "default-input-invalid-message",
    },
    render: (props) => <PaymentOwnerName {...props} />,
  },
  EnablePreview: {
    element: "customelement",
    fieldKey: "EnablePreview",
    render: (props) => (
      <SettingsItem
        textKey="enable-preview-text"
        disableOption={{ fieldName: "PaymentAmount" }}
        tooltip="jpay-enable-file-preview-tooltip"
        clickableTooltip={true}
        icon="eye-light"
        {...props}
      />
    ),
  },
  PaymentEachDownload: {
    element: "customelement",
    fieldKey: "PaymentEachDownload",
    render: (props) => (
      <SettingsItem
        textKey="no-paymentonse-text"
        icon="user-payment"
        disableOption={{ fieldName: "PaymentAmount" }}
        tooltip="jpay-request-each-user-tooltip"
        {...props}
      />
    ),
  },
};

export const securityFields = {
  MailPassword: {
    element: "customelement",
    fieldKey: "MailPassword",
    render: (props) => (
      <SecurityPasswordField
        icon="lock-light"
        subcribersOnly={true}
        {...props}
      />
    ),
  },
  BlockShare: {
    element: "customelement",
    fieldKey: "BlockShare",
    render: (props) => (
      <SettingsItem
        textKey="block-share-text"
        icon="social"
        disableOption={{
          fieldName: "Collaboration",
          conditionFunc: (value) => value === "recipients",
          tooltipOnDisable: true,
          turnOnDisable: true,
        }}
        tooltip="enabled-due-to-collaboration"
        {...props}
      />
    ),
  },
  LimitDownloads: {
    element: "customelement",
    fieldKey: "LimitDownloads",
    render: (props) => (
      <DownloadLimit icon="downloads" subcribersOnly={true} {...props} />
    ),
  },
  RecipientAccess: {
    element: "customelement",
    fieldKey: "RecipientAccess",
    render: (props) => (
      <SettingsItem
        textKey="recipient-access-text"
        icon="recipient"
        subcribersOnly={true}
        {...props}
        tooltip="recipient-access-only-tooltip"
      />
    ),
  },
  PreviewAccess: {
    element: "customelement",
    fieldKey: "PreviewAccess",
    render: (props) => (
      <SettingsItem
        textKey="preview-access-text"
        icon="eye-light"
        subcribersOnly={true}
        {...props}
        tooltip="preview-access-only-tooltip"
      />
    ),
  },
  Collaboration: {
    element: "customelement",
    fieldKey: "Collaboration",
    render: (props) => <CollaborationMailField {...props} />,
  },
};

export const settingsFields = {
  SaveMail: {
    element: "customelement",
    fieldKey: "SaveMail",
    render: (props) => (
      <SaveMailComponent textKey="save-mail-text" {...props} />
    ),
  },
  DisplayVideo: {
    element: "customelement",
    fieldKey: "DisplayVideo",
    render: (props) => <DisplayVideoField {...props} />,
  },
  NotifyDownload: {
    element: "customelement",
    fieldKey: "NotifyDownload",
    render: (props) => (
      <SettingsItem
        textKey="notify-download-text"
        {...props}
        icon="bell-light"
      />
    ),
  },
  ReceiveCopy: {
    element: "customelement",
    fieldKey: "ReceiveCopy",
    render: (props) => (
      <SettingsItem
        textKey="recieve-copy-text"
        {...props}
        icon="envelope-light"
      />
    ),
  },
};
